import React, { MouseEvent, ReactNode, useEffect } from 'react'
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuItemProps,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { Check, Circle, Error, Info, Warning } from '@mui/icons-material'
import { MessageSquare } from 'react-feather'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import NavbarMessageType from '../../enums/NavbarMessageType'
import { Announcement } from '../../types'
import { getActionRequestsByCompany, setCompanyRequests } from '../../redux/slices/requests'
import useDialog from '../../hooks/useDialog'
import { usePolicy } from '../../contexts/PolicyContext'
import useAuth from '../../hooks/useAuth'
import { humanizeTimestamp } from '../../utils/date'
import useMenu from '../../hooks/useMenu'
import useClosedAnnouncements from '../../hooks/useClosedAnnouncements'
import AnnouncementSeverity from '../../enums/AnnouncementSeverity'

interface AnnouncementMenuItemProps extends Omit<MenuItemProps, 'children' | 'onClick'> {
  onClick?: (event?: MouseEvent<HTMLLIElement>) => void
  announcement: Announcement
}

function AnnouncementMenuItem({ announcement, onClick, ...props }: AnnouncementMenuItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * The icon.
   */
  const icon =
    announcement.severity === AnnouncementSeverity.ERROR ? (
      <Error color="error" />
    ) : announcement.severity === AnnouncementSeverity.WARNING ? (
      <Warning color="warning" />
    ) : announcement.severity === AnnouncementSeverity.SUCCESS ? (
      <Check color="success" />
    ) : (
      <Info color="info" />
    )

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * Handle the "click" event.
   */
  const handleOpen = (event: MouseEvent<HTMLLIElement | HTMLButtonElement>) => {
    event.stopPropagation()

    openDialog()
  }

  /**
   * The closed announcements.
   */
  const closedAnnouncements = useClosedAnnouncements()

  /**
   * Handle the "close" action.
   */
  const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    if (currentUser) {
      const newClosedAnnouncements = [...closedAnnouncements]

      if (!newClosedAnnouncements.includes(announcement.id)) {
        newClosedAnnouncements.push(announcement.id)
      }

      window.localStorage.setItem(
        'closedAnnouncements',
        JSON.stringify({
          [currentUser.id]: newClosedAnnouncements
        })
      )
    }

    closeDialog()

    onClick?.()
  }

  return (
    <>
      <MessageMenuItem
        type={NavbarMessageType.ANNOUNCEMENT}
        title={t('announcement')}
        message={announcement.message}
        timestamp={new Date(announcement.showAt)}
        icon={icon}
        button={
          <Stack direction="row" alignItems="center" gap={1}>
            <Button variant="text" color="inherit" size="small" onClick={handleClose}>
              {t('hide')}
            </Button>

            <Button variant="text" color="primary" size="small" onClick={handleOpen}>
              {t('readMore')}
            </Button>
          </Stack>
        }
        {...props}
        onClick={handleOpen}
      />

      <Dialog open={open} maxWidth="md" fullWidth onClose={closeDialog}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {announcement.name}
            <Typography color="disabled">{humanizeTimestamp(announcement.showAt)}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {/* @ts-expect-error typed incorrectly*/}
          <Markdown rehypePlugins={[rehypeRaw]}>{announcement.message}</Markdown>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" onClick={handleClose}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

interface MessageMenuItemProps extends Omit<MenuItemProps, 'children'> {
  type: NavbarMessageType
  title?: string
  message?: string
  timestamp?: Date
  icon?: ReactNode
  button?: ReactNode
  navigationId?: number | string
  requestId?: string
}

function MessageMenuItem({
  title,
  message,
  timestamp,
  icon,
  button,
  navigationId: _navigationId,
  requestId: _requestId,
  type: _type,
  ...props
}: MessageMenuItemProps) {
  return (
    <MenuItem {...props} sx={{ py: 2 }}>
      {icon && <ListItemIcon>{icon}</ListItemIcon>}
      <ListItemText
        sx={{
          pr: '0 !important',
          textWrap: 'nowrap !important',
          textOverflow: 'ellipsis !important',
          overflow: 'hidden !important'
        }}
        primary={
          <Stack direction="column" gap={1}>
            <Typography
              title={message}
              sx={{
                textWrap: 'nowrap !important',
                textOverflow: 'ellipsis !important',
                overflow: 'hidden !important'
              }}
            >
              {message}
            </Typography>
            {button}
          </Stack>
        }
        secondary={
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="start"
            gap={2}
            component={'div'}
          >
            {title && (
              <>
                <Typography variant="subtitle2" component="div">
                  {title}
                </Typography>
                <Circle sx={{ fontSize: '6px' }} />
              </>
            )}

            {timestamp && (
              <Tooltip title={humanizeTimestamp(timestamp)}>
                <Typography>{formatDistanceToNow(timestamp, { addSuffix: true })}</Typography>
              </Tooltip>
            )}
          </Stack>
        }
        secondaryTypographyProps={{
          sx: {
            textWrap: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
          },
          component: 'div'
        }}
      />
    </MenuItem>
  )
}

interface NavbarMessagesDropDownProps {
  announcements: Announcement[]
}

export default function NavbarMessagesDropdown({ announcements }: NavbarMessagesDropDownProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The menu state.
   */
  const { isMenuOpen, anchorEl, openMenu, closeMenu } = useMenu()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * The policies.
   */
  const { isSuperadmin, isAdmin } = usePolicy()

  /**
   * The closed announcements.
   */
  const closedAnnouncements = useClosedAnnouncements()

  /**
   * The open annnouncements.
   */
  const shownAnnouncements = announcements.filter(
    (announcement) => !closedAnnouncements.includes(announcement.id)
  )

  /**
   * Close all announcements.
   */
  const dismissAllAnnouncements = () => {
    if (currentUser) {
      const newClosedAnnouncements = [...closedAnnouncements]

      announcements.forEach((announcement) => {
        if (!newClosedAnnouncements.includes(announcement.id)) {
          newClosedAnnouncements.push(announcement.id)
        }
      })

      window.localStorage.setItem(
        'closedAnnouncements',
        JSON.stringify({
          [currentUser.id]: newClosedAnnouncements
        })
      )
    }
  }

  /**
   * Handle the "Dismiss all" action.
   */
  const handleDismissAll = () => {
    // Dismiss all announcements.
    dismissAllAnnouncements()

    closeMenu()
  }

  useEffect(() => {
    if (currentUser?.company && (isAdmin || isSuperadmin)) {
      dispatch(getActionRequestsByCompany(currentUser.company.id))
    } else {
      dispatch(setCompanyRequests([]))
    }
  }, [currentUser])

  return (
    <React.Fragment>
      <Tooltip title={t('messages')}>
        <IconButton color="inherit" onClick={openMenu} size="large">
          <Badge badgeContent={shownAnnouncements.length} color="error">
            <MessageSquare />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        open={isMenuOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: {
            width: 360
          }
        }}
        onClose={closeMenu}
      >
        {shownAnnouncements.length === 0 ? (
          <Box p={2}>
            <Typography variant="subtitle1" color="textPrimary" textAlign="center">
              {t('youHaveNoMessages')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ px: 4 }}>
            <Button
              variant="text"
              color="inherit"
              size="small"
              fullWidth
              onClick={handleDismissAll}
            >
              {t('hideAll')}
            </Button>
          </Box>
        )}

        {shownAnnouncements.map((announcement) => (
          <AnnouncementMenuItem
            key={announcement.id}
            announcement={announcement}
            onClick={closeMenu}
          />
        ))}
      </Menu>
    </React.Fragment>
  )
}
